<template>
  <router-link
    v-if="isActive"
    :to="{ path: nextPage, query: { name: productName } }"
    class="navigation-card__link"
    @click="handleClick"
  >
    <NavigationCardElement
      :image-source="imageSource"
      :title="title"
      :icon="icon"
    />
  </router-link>

  <div
    v-else
    class="navigation-card"
    @click="handleInactiveClick"
  >
    <div class="navigation-card__disabled">
      <NavigationCardElement
        :image-source="imageSource"
        :title="title"
        :icon="icon"
      />
      <div class="navigation-card__overlay">
        <div class="navigation-card__cta">
          <h2 class="navigation-card__cta-title">{{ ctaTitle }}</h2>
          <h3 class="navigation-card__cta-subtitle">{{ ctaSubtitle }}</h3>
          <ul class="navigation-card__cta-benefits">
            <li
              v-for="benefit in ctaBenefits"
              :key="benefit"
            >
              {{ benefit }}
            </li>
          </ul>
          <button class="navigation-card__cta-button">Kup teraz</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductName } from '@common/contracts';
import NavigationCardElement from './NavigationCardElement.vue';
import { PropType } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  imageSource: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  productName: {
    type: String as PropType<ProductName>,
    required: true,
    validator: (value: string) => Object.values(ProductName).includes(value as ProductName),
  },
  icon: {
    type: String,
    required: true,
  },
  nextPage: {
    type: String,
    required: true,
  },
  isActive: {
    type: Boolean,
    required: true,
  },
  materialPath: {
    type: String,
    default: '',
  },
  ctaTitle: {
    type: String,
    default: '',
  },
  ctaSubtitle: {
    type: String,
    default: '',
  },
  ctaBenefits: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const router = useRouter();
const emits = defineEmits<{
  click: [];
  buyProduct: [];
}>();

function buyProduct(name: ProductName) {
  console.log(name);
  emits('buyProduct');
  window.open('https://sklep.wspomnieniazwesela.pl/', '_blank', 'noopener,noreferrer');
}

async function handleClick(event: MouseEvent) {
  emits('click');
  if (props.materialPath) {
    event.preventDefault();
    try {
      const link = document.createElement('a');
      link.href = props.materialPath;
      link.download = `${props.productName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading material:', error);
    }
  }

  router.push({ path: props.nextPage, query: { name: props.productName } });
}

function handleInactiveClick() {
  buyProduct(props.productName);
}
</script>

<style lang="scss" scoped>
.navigation-card {
  z-index: 2;

  @media (max-width: $media-max-tablet) {
    width: 100%;
    height: 100%;
  }

  &__overlay {
    position: absolute;
    top: -10px;
    left: 0;
    width: 100%;
    height: auto;
    background: linear-gradient(135deg, rgba($brown-200, 0.95) 0%, rgba($brown-100, 0.95) 100%);
    color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    font-weight: 700;
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    z-index: 10;
    text-shadow: 0 2px 4px rgba($black-100, 0.3);
    padding: 2rem;
    border-radius: 10px;

    &:hover {
      opacity: 1;
      pointer-events: auto;
    }

    @media (max-width: $media-max-tablet) {
      font-size: 2rem;
      padding: 1.5rem;
      height: 100%;
      top: 0;
      opacity: 1;
      pointer-events: auto;
    }

    @media (max-width: $media-max-phone) {
      font-size: 1.75rem;
      padding: 1rem;
    }
  }

  &__cta {
    text-align: center;
    max-width: 500px;
    width: 100%;
    padding: 1rem;
    z-index: 20;

    @media (max-width: $media-max-tablet) {
      display: flex;
      flex-direction: column;
    }

    &-title {
      font-size: 2.8rem;
      font-weight: 700;
      margin-bottom: 0.8rem;
      color: $white;
      text-shadow: 0 2px 4px rgba($black-100, 0.3);
      line-height: 1.2;

      @media (max-width: $media-max-tablet) {
        font-size: 2.4rem;
        display: flex;
        flex-direction: column;
      }

      @media (max-width: $media-max-phone) {
        font-size: 2rem;
      }
    }

    &-subtitle {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      color: $white;
      text-shadow: 0 2px 4px rgba($black-100, 0.3);
      line-height: 1.3;

      @media (max-width: $media-max-tablet) {
        font-size: 1.8rem;
      }

      @media (max-width: $media-max-phone) {
        font-size: 1.6rem;
      }
    }

    &-benefits {
      list-style: none;
      padding: 0;
      margin: 0 0 2rem;
      text-align: left;
      display: inline-block;

      li {
        color: $white;
        font-size: 1.6rem;
        margin-bottom: 0.8rem;
        padding-left: 2rem;
        position: relative;
        text-shadow: 0 1px 2px rgba($black-100, 0.3);
        line-height: 1.4;

        &::before {
          content: '•';
          color: $white;
          font-size: 1.8rem;
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }

        @media (max-width: $media-max-tablet) {
          font-size: 1.4rem;
        }

        @media (max-width: $media-max-phone) {
          font-size: 1.2rem;
        }
      }
    }

    &-button {
      display: inline-block;
      background: $white;
      color: $brown-200;
      font-size: 1.6rem;
      font-weight: 600;
      padding: 1rem 2.5rem;
      border-radius: 0.8rem;
      text-decoration: none;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
      border: none;
      cursor: pointer;
      margin-top: 1rem;
      width: 15rem;
      align-self: center;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba($black-100, 0.3);
      }

      @media (max-width: $media-max-tablet) {
        font-size: 1.4rem;
        padding: 0.8rem 2rem;
      }

      @media (max-width: $media-max-phone) {
        font-size: 1.2rem;
        padding: 0.7rem 1.8rem;
      }
    }
  }

  &__disabled {
    position: relative;
    filter: grayscale(100%) brightness(0.8);
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: $media-max-tablet) {
      filter: grayscale(0%) brightness(1);
      width: 100%;
      height: 100%;
    }

    &:hover {
      filter: grayscale(0%) brightness(1);
      transform: translateY(-5px);

      @media (max-width: $media-max-tablet) {
        transform: translateY(-3px);
      }

      .navigation-card__overlay {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  &__link {
    text-decoration: none;
    transition: all 0.3s ease;
    display: block;
    width: auto;
    z-index: 1;

    @media (max-width: $media-max-tablet) {
      width: 100%;
    }

    &:hover {
      transform: translateY(-5px);

      @media (max-width: $media-max-tablet) {
        transform: translateY(-3px);
      }
    }
  }
}
</style>
